﻿import flatten from 'lodash.flatten';
﻿import moment from 'moment';

import LfDataService from '../../../Services/LfDataService';
import LfWebService from '../../../Services/LfWebService';

import {
    pageIsLoadingAction,
    pageErrorAction,
    pageHasErroredAction
} from '../../../State/actions';

import {
    sortInsurancesByName, 
    sortBreedsById,
    fixCamelCasedGroupKeys,
} from '../../../DataHelpers/lfDataHelpers';

import sortByInsuranceMetaOrder from '../../../Utils/sortByInsuranceMetaOrder';

export function loadStartPageDataAction() {
    return (dispatch) => {
        const lfDataService = new LfDataService();
        const lfWebService = new LfWebService(); 

        let memberInfo, currentCoInsured; 

        dispatch(pageIsLoadingAction(true));

        lfDataService.memberInfo()
            .then(data => {
                memberInfo = data.memberInfo;
                currentCoInsured = data.currentCoInsured;

                return Promise.all([
                    lfDataService.possessions(),
                    lfDataService.insurances(),
                    lfDataService.quotes(memberInfo.zipCode),
                    lfDataService.animalBreeds('all'),
                    lfWebService.insuranceMetaData().then(data => ({ metaData: fixCamelCasedGroupKeys(data.insurances) }))
                ]);
            })
            .then(values => {
                let data = values.reduce((acc, obj) => {
                    Object.keys(obj).forEach(prop => acc[prop] = obj[prop]);
                    return acc;
                }, {});

                const possessions = data.possessions.filter(p => p.insuranceName != null);
                const insurancesByName = sortInsurancesByName(data.insurances);

                const currentPossessions = possessions
                    .filter(i => moment(i.fromDate).isSameOrBefore(moment(), 'day'))
                    .sort(sortByInsuranceMetaOrder(insurancesByName, data.metaData, []));

                const futurePossessions = possessions
                    .filter(i => moment(i.fromDate).isAfter(moment(), 'day'))
                    .sort(sortByInsuranceMetaOrder(insurancesByName, data.metaData, []));

                const currentPremiumTotal = currentPossessions.map(pos => { return pos.premiumPeriod === 'monthly' ? pos.premium : 0; }).reduce((sum, val) => sum + val, 0);

                dispatch(startPageLoadedAction({
                    ...data,
                    memberInfo,
                    currentCoInsured,
                    insurancesByName,
                    possessions,
                    quotesByName: sortInsurancesByName(data.quotes),
                    possessionsByName: sortInsurancesByName(possessions),
                    currentPossessions, 
                    futurePossessions,
                    currentPremiumTotal,
                    breedsById: sortBreedsById(data.breeds),
                    insurancePurchaseUrl: lfWebService.insurancePurchaseUrl(),
                    insurancePreregistrationsUrl: lfWebService.insurancePreregistrationsUrl()
                }));

                dispatch(pageIsLoadingAction(false));
                dispatch(loadAnimalPossessionQuotes(possessions, insurancesByName));
            }) 
            .catch(e => {
                dispatch(pageIsLoadingAction(false));
                dispatch(pageErrorAction(e));
                dispatch(pageHasErroredAction(true));
            });
    };
}

export function startPageLoadedAction(data) {
    return {
        type: 'STARTPAGE_LOADED',
        data
    };
}

export function loadAnimalPossessionQuotes() {
    const lfDataService = new LfDataService();

    return (dispatch, getState) => {
        const { startPageData } = getState();

        const possessions = startPageData.possessions;
        const insurancesByName = startPageData.insurancesByName;

        let upgradeablePossessions = possessions.filter(p => {
            const groupName = insurancesByName[p.insuranceName].groupName;
            const possibleUpgrades = insurancesByName[p.insuranceName].possibleUpgrades;
            return animalInsuranceGroups.indexOf(groupName) > -1 && possibleUpgrades.length > 0;
        });

        let animalQuotePromises = flatten(upgradeablePossessions.map(p =>
            insurancesByName[p.insuranceName].possibleUpgrades.map(upgradeInsuranceName => (

                lfDataService.insuranceObjectQuote(upgradeInsuranceName, p.insuranceObjectId)
                    .then(data => ({ // format data by insurance obj id for Promise.all() resolve
                        [p.insuranceObjectId]: {
                            insuranceName: upgradeInsuranceName,
                            quote: data.quote
                        }
                    }))
                    .catch(e => {
                        console.error(`Could not fetch animal quote for insurance object id : ${p.insuranceObjectId}`, e);
                    })
            ))
        ));

        Promise.all(animalQuotePromises)
            .then(data => { // data is now an object of quotes ordered by ins. obj. id
                const quotes = data.reduce((quoteObj, quote) => {
                    Object.keys(quote).forEach(insuranceObjectId => {
                        const quoteInsuranceName = quote[insuranceObjectId].insuranceName;
                        const currentQuote = quote[insuranceObjectId].quote;

                        quoteObj[insuranceObjectId] = {
                            ...quoteObj[insuranceObjectId],
                            [quoteInsuranceName]: currentQuote
                        }
                    });

                    return quoteObj;
                }, {});

                dispatch(animalPossessionQuoteDataLoaded(quotes));
            });
    }
}

export function animalPossessionQuoteDataLoaded(quotes) {
    return {
        type: 'ANIMAL_POSSESSION_QUOTES_LOADED',
        quotes
    }
}